
import React, { useState, useMemo } from 'react';
import { Users, TrendingUp, Clock, BarChart3, Filter } from 'lucide-react';
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const DetailedSegmentAnalysis = () => {
  const [selectedSegment, setSelectedSegment] = useState('urban');
  const [selectedMetric, setSelectedMetric] = useState('volume');
  const [selectedFuelType, setSelectedFuelType] = useState('all');
  
  // Sample data for segments
  const segments = [
    { id: 'urban', name: 'Urban Premium', color: '#8B5CF6' },
    { id: 'highway', name: 'Highway Transit', color: '#3B82F6' },
    { id: 'rural', name: 'Rural Standard', color: '#10B981' },
    { id: 'suburban', name: 'Suburban Mixed', color: '#F59E0B' },
  ];
  
  // Sample data for CLV
  const clvData = {
    urban: { value: 8.7, trend: '+1.2%', color: '#8B5CF6' },
    highway: { value: 6.4, trend: '-0.8%', color: '#3B82F6' },
    rural: { value: 5.3, trend: '+0.6%', color: '#10B981' },
    suburban: { value: 7.1, trend: '+0.4%', color: '#F59E0B' },
  };
  
  // Base trend data
  const baseTrendData = [
    { month: 'Jan', urban: 105, highway: 98, rural: 89, suburban: 92 },
    { month: 'Feb', urban: 107, highway: 95, rural: 91, suburban: 93 },
    { month: 'Mar', urban: 109, highway: 93, rural: 93, suburban: 94 },
    { month: 'Apr', urban: 108, highway: 91, rural: 94, suburban: 93 },
    { month: 'May', urban: 110, highway: 89, rural: 96, suburban: 92 },
    { month: 'Jun', urban: 112, highway: 87, rural: 97, suburban: 91 },
  ];
  
  // Process trend data based on selected fuel type
  const trendData = useMemo(() => {
    if (selectedFuelType === 'all' || selectedMetric !== 'volume') {
      return baseTrendData;
    }
    
    // Apply multipliers based on fuel type selection
    let multiplier = 1.0;
    switch(selectedFuelType) {
      case 'petrol':
        multiplier = 0.6; // 60% petrol
        break;
      case 'diesel':
        multiplier = 0.4; // 40% diesel
        break;
      case 'premium':
        multiplier = 0.15; // 15% premium fuels
        break;
      case 'e20':
        multiplier = 0.12; // 12% E20
        break;
      case 'e85':
        multiplier = 0.08; // 8% E85
        break;
      case 'ev':
        multiplier = 0.05; // 5% EV charging
        break;
      case 'lpg':
        multiplier = 0.2; // 20% LPG
        break;
      case 'ngv':
        multiplier = 0.1; // 10% NGV
        break;
      default:
        multiplier = 1.0;
    }
    
    // Apply the multiplier to all segment values
    return baseTrendData.map(item => ({
      month: item.month,
      urban: Math.round(item.urban * multiplier),
      highway: Math.round(item.highway * multiplier),
      rural: Math.round(item.rural * multiplier),
      suburban: Math.round(item.suburban * multiplier)
    }));
  }, [baseTrendData, selectedFuelType, selectedMetric]);
  
  // Sample data for demographics
  const demographicsData = {
    urban: [
      { name: 'Affluent Urban', value: 38 },
      { name: 'Young Professionals', value: 27 },
      { name: 'Commuters', value: 22 },
      { name: 'Tourists', value: 13 },
    ],
    highway: [
      { name: 'Transit Commuters', value: 42 },
      { name: 'Truckers', value: 31 },
      { name: 'Travelers', value: 18 },
      { name: 'Locals', value: 9 },
    ],
    rural: [
      { name: 'Rural Residents', value: 52 },
      { name: 'Farmers', value: 23 },
      { name: 'Small Business', value: 15 },
      { name: 'Travelers', value: 10 },
    ],
    suburban: [
      { name: 'Families', value: 41 },
      { name: 'Professionals', value: 29 },
      { name: 'Retirees', value: 18 },
      { name: 'Students', value: 12 },
    ],
  };
  
  // Sample data for peak hours
  const peakHoursData = {
    urban: [
      { hour: '6-8am', volume: 85 },
      { hour: '8-10am', volume: 92 },
      { hour: '10am-12pm', volume: 78 },
      { hour: '12-2pm', volume: 82 },
      { hour: '2-4pm', volume: 75 },
      { hour: '4-6pm', volume: 95 },
      { hour: '6-8pm', volume: 100 },
      { hour: '8-10pm', volume: 88 },
    ],
    highway: [
      { hour: '6-8am', volume: 80 },
      { hour: '8-10am', volume: 88 },
      { hour: '10am-12pm', volume: 92 },
      { hour: '12-2pm', volume: 100 },
      { hour: '2-4pm', volume: 95 },
      { hour: '4-6pm', volume: 90 },
      { hour: '6-8pm', volume: 85 },
      { hour: '8-10pm', volume: 75 },
    ],
    rural: [
      { hour: '6-8am', volume: 95 },
      { hour: '8-10am', volume: 85 },
      { hour: '10am-12pm', volume: 75 },
      { hour: '12-2pm', volume: 72 },
      { hour: '2-4pm', volume: 70 },
      { hour: '4-6pm', volume: 80 },
      { hour: '6-8pm', volume: 90 },
      { hour: '8-10pm', volume: 65 },
    ],
    suburban: [
      { hour: '6-8am', volume: 90 },
      { hour: '8-10am', volume: 100 },
      { hour: '10am-12pm', volume: 75 },
      { hour: '12-2pm', volume: 70 },
      { hour: '2-4pm', volume: 65 },
      { hour: '4-6pm', volume: 85 },
      { hour: '6-8pm', volume: 95 },
      { hour: '8-10pm', volume: 75 },
    ],
  };
  
  // Colors for pie chart
  const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B'];
  
  const selectedSegmentData = segments.find(s => s.id === selectedSegment);
  const selectedClvData = clvData[selectedSegment];
  const selectedDemographicsData = demographicsData[selectedSegment];
  const selectedPeakHoursData = peakHoursData[selectedSegment];
  
  // Get product label for display
  const getProductLabel = () => {
    switch(selectedFuelType) {
      case 'petrol': return 'Petrol Only';
      case 'diesel': return 'Diesel Only';
      case 'premium': return 'Premium Fuels';
      case 'e20': return 'E20 Gasohol';
      case 'e85': return 'E85 Gasohol';
      case 'ev': return 'EV Charging';
      case 'lpg': return 'LPG';
      case 'ngv': return 'NGV (Natural Gas)';
      case 'all':
      default: return 'All Products';
    }
  };
  
  return (
    <div>
      {/* Segment Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center mb-4">
          <Filter size={20} className="text-purple-600 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">Segment Filter</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {segments.map(segment => (
            <button
              key={segment.id}
              onClick={() => setSelectedSegment(segment.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedSegment === segment.id 
                  ? `bg-${segment.color} text-white` 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={selectedSegment === segment.id ? {backgroundColor: segment.color} : {}}
            >
              {segment.name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Top Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Segment Profile */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users size={20} className="mr-2 text-purple-600" />
            Segment Profile
          </h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Segment</p>
              <h4 className="text-xl font-medium text-gray-900">{selectedSegmentData.name}</h4>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Customer Lifetime Value</p>
              <div className="flex items-baseline">
                <h4 className="text-xl font-medium text-gray-900 mr-2">{selectedClvData.value}x</h4>
                <span className={`text-sm font-medium ${selectedClvData.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedClvData.trend}
                </span>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Segment Size</p>
              <h4 className="text-xl font-medium text-gray-900">
                {segments.find(s => s.id === selectedSegment).name === 'Urban Premium' ? '523' : 
                segments.find(s => s.id === selectedSegment).name === 'Highway Transit' ? '682' : 
                segments.find(s => s.id === selectedSegment).name === 'Rural Standard' ? '754' : '384'} Stations
              </h4>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Avg. Transaction Value</p>
              <h4 className="text-xl font-medium text-gray-900">
                {segments.find(s => s.id === selectedSegment).name === 'Urban Premium' ? '850 ฿' : 
                segments.find(s => s.id === selectedSegment).name === 'Highway Transit' ? '780 ฿' : 
                segments.find(s => s.id === selectedSegment).name === 'Rural Standard' ? '520 ฿' : '650 ฿'}
              </h4>
            </div>
          </div>
        </div>
        
        {/* Performance Trend */}
        <div className="md:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <TrendingUp size={20} className="mr-2 text-purple-600" />
              Performance Trend
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={() => setSelectedMetric('volume')}
                className={`px-3 py-1 rounded-md text-xs font-medium ${selectedMetric === 'volume' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Volume
              </button>
              <button 
                onClick={() => setSelectedMetric('cafe')}
                className={`px-3 py-1 rounded-md text-xs font-medium ${selectedMetric === 'cafe' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Café Amazon
              </button>
            </div>
          </div>
          
          {/* Product filter - only shown when volume metric is selected */}
          {selectedMetric === 'volume' && (
            <div className="flex justify-end mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Product:</span>
                <select 
                  className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs"
                  value={selectedFuelType}
                  onChange={(e) => setSelectedFuelType(e.target.value)}
                >
                  <option value="all">All Products</option>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="premium">Premium Fuels</option>
                  <option value="e20">E20 Gasohol</option>
                  <option value="e85">E85 Gasohol</option>
                  <option value="lpg">LPG</option>
                  <option value="ngv">NGV</option>
                  <option value="ev">EV Charging</option>
                </select>
              </div>
            </div>
          )}
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="urban" name="Urban Premium" fill="#8B5CF6" />
                <Bar dataKey="highway" name="Highway Transit" fill="#3B82F6" />
                <Bar dataKey="rural" name="Rural Standard" fill="#10B981" />
                <Bar dataKey="suburban" name="Suburban Mixed" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Display selected product when volume metric is selected */}
          {selectedMetric === 'volume' && selectedFuelType !== 'all' && (
            <div className="mt-2 text-xs text-gray-500 text-center">
              Showing data for: {getProductLabel()}
            </div>
          )}
        </div>
      </div>
      
      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Demographics */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users size={20} className="mr-2 text-purple-600" />
            Customer Demographics
          </h3>
          
          <div className="h-80 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={selectedDemographicsData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {selectedDemographicsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Peak Hours */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Clock size={20} className="mr-2 text-purple-600" />
            Peak Hours Analysis
          </h3>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={selectedPeakHoursData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar 
                  dataKey="volume" 
                  name="Transaction Volume (%)" 
                  fill={selectedSegmentData.color}
                  radius={[4, 4, 0, 0]} 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedSegmentAnalysis;
